import React, {useCallback} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {Subject} from "rxjs";
import {showToast} from "common";
import _ from "lodash";
import {useObservable} from "rxsupport";
import {ReportModal} from "../../form-components/ReportModal";
import {ModalTextAreaInput} from "../../form-components/ModalTextAreaInput";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {LoadingSpinner} from "common";
import {ReportStatusInquiryInput, AuthorizationInfo} from "../../domain";
import {Controller} from "react-hook-form";
import {DropdownMultiSelect, SimpleDropdown} from "common";
import {SPLIT_DELIMITER, splitAndRemoveEmpty} from "../../../../placement/const";

const REPORT_TYPE_OPTIONS = new Map<string, string>([
    ["Addendum Report", "Addendum Report"],
    ["Assessment Report", "Assessment Report"],
    ["Change Hours Addendum", "Change Hours Addendum"],
    ["Discharge Report", "Discharge Report"],
    ["Progress Report", "Progress Report"],
    ["Transfer Report", "Transfer Report"],
    ["Transfer Re-evaluation Report", "Transfer Re-evaluation Report"],
]);

const REPORT_INQUIRY_OPTIONS = ["Status of Report", "Question about report revisions", "Other"];

type Props = {
    visible$: Subject<boolean>;
    authorization: AuthorizationInfo;
    onFinished: (formValues: ReportStatusInquiryInput | null) => Promise<void>;
};

export function ReportStatusInquiryFormModal({authorization, visible$, onFinished}: Props) {
    const visible = useObservable(visible$, false);

    const methods = useForm<ReportStatusInquiryInput>({
        defaultValues: {
            reportType: "",
            reportStatusInquiry: "",
            additionalDetails: "",
        },
    });

    const {promiseInProgress} = usePromiseTracker();
    const submitWithTracking = useCallback(
        (formValues: ReportStatusInquiryInput | null) => trackPromise(onFinished(formValues)),
        [onFinished]
    );

    const onCancel = useCallback(async () => {
        showToast.warning(`Submission for Report Status Inquiry is cancelled`);
        methods.reset();
        visible$.next(false);
    }, [methods, visible$]);

    const submit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            await methods.handleSubmit(async (data: ReportStatusInquiryInput) => {
                await submitWithTracking(
                    _.pick(data, ["reportType", "reportStatusInquiry", "additionalDetails"])
                );
                methods.reset();
                visible$.next(false);
            })(e);
        },
        [methods, visible$, submitWithTracking]
    );

    return (
        <ReportModal visible={visible} title={"Report Status Inquiry"} onClose={onCancel}>
            <FormProvider {...methods}>
                <form autoComplete="off" onSubmit={submit}>
                    {!promiseInProgress && (
                        <>
                            <div className="space-y-6">
                                <div className="flex flex-col w-full p-4 scrollbar-always" style={{maxHeight: "calc(100vh - 68px - 100px - 37px - 90px - 60px)"}}>
                                    <ul className="space-y-2">
                                        <li className="w-full flex flex-col text-base py-2 text-black">
                                            <label className="block mb-2">
                                                What type of report are you following up on? *
                                            </label>
                                            <div className="w-full">
                                                <Controller<ReportStatusInquiryInput, "reportType">
                                                    name="reportType"
                                                    control={methods.control}
                                                    rules={{
                                                        required: {
                                                            value: true,
                                                            message: "Please select an option",
                                                        },
                                                    }}
                                                    render={({
                                                        field: {value, onChange},
                                                        fieldState: {error},
                                                    }) => (
                                                        <>
                                                            <SimpleDropdown
                                                                options={REPORT_TYPE_OPTIONS}
                                                                selected={value || ""}
                                                                disabled={promiseInProgress}
                                                                hasErrorBorder={!!error}
                                                                setSelected={(selected) => onChange(selected)}
                                                                textColor={"black-200"}
                                                                placeholder="--Select--"
                                                            />
                                                            {error && (
                                                                <div className="text-red-700 mt-1">
                                                                    {error.message}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                        </li>
                                        <li className="w-full flex flex-col text-base py-2 text-black">
                                            <div className="block mb-2">
                                                What type of inquiry do you have? *
                                            </div>
                                            <div className="w-full">
                                                <Controller<ReportStatusInquiryInput, "reportStatusInquiry">
                                                    name="reportStatusInquiry"
                                                    control={methods.control}
                                                    rules={{
                                                        required: {
                                                            value: true,
                                                            message: "Please select one or more options",
                                                        },
                                                    }}
                                                    render={({
                                                        field: {value, onChange},
                                                        fieldState: {error},
                                                    }) => (
                                                        <>
                                                            <DropdownMultiSelect
                                                                field={"reportStatusInquiry"}
                                                                options={REPORT_INQUIRY_OPTIONS}
                                                                labelMapperFunc={(val) => val}
                                                                value={value || ""}
                                                                disabled={promiseInProgress}
                                                                showBadges={true}
                                                                width="w-full"
                                                                minWidth="min-w-xlplus"
                                                                placeholder="--Select--"
                                                                onRemoveBadge={(opt) => {
                                                                    const current = splitAndRemoveEmpty(value || "");
                                                                    const updated = current.filter((o) => o !== opt);
                                                                    onChange(updated.join(SPLIT_DELIMITER));
                                                                }}
                                                                setValue={(selected) => {
                                                                    onChange(splitAndRemoveEmpty(
                                                                            selected
                                                                        ).join(SPLIT_DELIMITER)
                                                                    );
                                                                }}
                                                            />
                                                            {error && (
                                                                <div className="text-red-700 mt-1">
                                                                    {error.message}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                        </li>
                                        <li className="w-full flex flex-col text-base py-2 text-black">
                                            <ModalTextAreaInput
                                                disabled={promiseInProgress}
                                                field="additionalDetails"
                                                label="Please provide additional details of the report you're inquiring about *"
                                                labelPosition="top"
                                                rowsNum={4}
                                                maxLength={4000}
                                                required
                                                requiredMessage="Please provide additional details about the report you are inquiring about before submitting"
                                                showCharacterCount
                                            />
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                                <div className="flex w-1/4">
                                    <FatOutlineButton color={"red"} text={"Cancel"} onClick={onCancel} />
                                    <span style={{width: 10}} />
                                    <FatOutlineButton type="submit" text={"Submit"} color={"green"} />
                                </div>
                            </div>
                        </>
                    )}

                    {promiseInProgress && (
                        <div className="p-24">
                            <LoadingSpinner text="Processing" />
                        </div>
                    )}
                </form>
            </FormProvider>
        </ReportModal>
    );
}
